import { useState } from 'react';
import { Radio, RadioChangeEvent } from 'antd';
import { Button } from '../button';
import { Accordion } from '../accordion';
import { useTheme } from '../../providers/theme';
import { getColors } from '../../components/utils/getColors';
import { LambdaAnalysis } from '../../components/dashboard/components/LambdaAnalysis';
import { LiaoAnalysis } from '../../components/dashboard/components/LiaoAnalysis';
import { useSelected } from '../../providers/selectedContext';
import styles from './CapillaryPressurePanel.module.css';

const ANALYSIS_OPTIONS = [
  { label: 'Liao Analysis', value: false },
  { label: 'Brooks-Corey Analysis', value: true },
];

const CapillaryPressurePanel = () => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo } = useSelected();
  const sample = selectedInfo?.sample;

  const [showCompanion, setShowCompanion] = useState(false);
  const [showLambda, setShowLambda] = useState(false);

  const hasLiao = !!sample?.analysis_values?.liao;
  const hasLambda = !!sample?.analysis_values?.lambda_values;
  const hasPcAnalysis = hasLiao || hasLambda;
  const hasPcCurves = (sample?.input_sample_curves?.pc_model?.pc?.length ?? 0) > 0;

  if (!hasPcAnalysis && !hasPcCurves) {
    return (
      <Accordion defaultOpen="capillary-pressure">
        <Accordion.Item id="capillary-pressure" header="Capillary Pressure">
          <p className={styles.noDataNotice} style={{ color: colors.text }}>
            No sample measured data registered. Go to <strong>Sample Measurements</strong> to add Sw and Pc values.
          </p>
        </Accordion.Item>
      </Accordion>
    );
  }

  if (!hasPcAnalysis) {
    return (
      <Accordion defaultOpen="capillary-pressure">
        <Accordion.Item id="capillary-pressure" header="Capillary Pressure">
          <p className={styles.noDataNotice} style={{ color: colors.text }}>
            Pc curves loaded, but no data detected. Check <strong>Pc Variables</strong> for invalid values.
          </p>
        </Accordion.Item>
      </Accordion>
    );
  }

  const companionLabel = showCompanion
    ? (showLambda ? 'Hide Liao data' : 'Hide Brooks-Corey data')
    : (showLambda ? 'Show Liao data' : 'Show Brooks-Corey data');

  return (
    <Accordion defaultOpen="capillary-pressure">
      <Accordion.Item id="capillary-pressure" header="Capillary Pressure">
        <Button variant="secondary" onClick={() => setShowCompanion(prev => !prev)}>
          {companionLabel}
        </Button>

        <Radio.Group
          className={`${styles.radioGroup} ${isDay ? 'day-radio' : 'night-radio'}`}
          onChange={(e: RadioChangeEvent) => setShowLambda(e.target.value)}
          options={ANALYSIS_OPTIONS}
          defaultValue={false}
          block
          optionType="button"
          buttonStyle="solid"
        />

        <LambdaAnalysis show={showLambda} showLiao={showCompanion} />
        <LiaoAnalysis show={!showLambda} showLambda={showCompanion} />
      </Accordion.Item>
    </Accordion>
  );
};

export default CapillaryPressurePanel;
